import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Gift, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * 18-B2 item 6: the Perks tab. Only ever mounted when GET /perks says
 * enabled (pilot area + tier are re-checked server-side on redeem anyway).
 * Copy rule: a thank-you, never a price, never "earn", never cash.
 */
export const PerksTab = ({ perks, onRefresh }) => {
  const [till, setTill] = useState(null); // the "show at the till" payload
  const [busy, setBusy] = useState(false);

  const redeem = async (offer) => {
    setBusy(true);
    try {
      const res = await api.redeemPerk(offer.id);
      setTill(res.redemption);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl" data-testid="perks-tab">
      <p className="text-sm text-neutral-600">
        Local businesses in <b>{perks.area}</b> saying thank you to neighbours who show up.
        These are small tokens of appreciation — nothing to buy, nothing to earn.
      </p>
      <div className="mt-6 space-y-4">
        {perks.offers.length === 0 && (
          <div className="text-sm text-neutral-500" data-testid="perks-empty">
            No thank-yous available at your tier just yet — keep showing up.
          </div>
        )}
        {perks.offers.map((o) => (
          <div key={o.id} data-testid={`perk-offer-${o.id}`} className="bg-white border border-neutral-200 rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="font-display text-lg text-forest flex items-center gap-2">
                <Gift className="w-4 h-4 text-terracotta" /> {o.partner_name}
              </div>
              <p className="text-sm text-neutral-700 mt-1">
                A thank-you from {o.partner_name} — because you showed up.
              </p>
              <p className="text-sm text-neutral-600 mt-1.5">{o.offer_text}</p>
              <div className="text-xs text-neutral-400 mt-2">
                {o.remaining_this_month} left this month · {o.min_tier.charAt(0).toUpperCase() + o.min_tier.slice(1)} tier and up
              </div>
            </div>
            <Button
              data-testid={`perk-redeem-${o.id}`}
              onClick={() => redeem(o)}
              disabled={busy || o.remaining_this_month === 0}
              className="rounded-full bg-forest hover:bg-forest-hover shrink-0"
            >
              {o.remaining_this_month === 0 ? "Used this month" : "Redeem"}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!till} onOpenChange={(v) => !v && setTill(null)}>
        <DialogContent data-testid="perk-till-screen" className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-display text-forest text-center">Show this at the till</DialogTitle>
          </DialogHeader>
          {till && (
            <div className="py-2">
              <div className="bg-cream border-2 border-dashed border-forest/30 rounded-2xl p-6">
                <Gift className="w-8 h-8 text-terracotta mx-auto" />
                <div className="font-display text-xl text-forest mt-3" data-testid="till-partner">{till.partner_name}</div>
                <p className="text-sm text-neutral-700 mt-1">{till.offer_text}</p>
                <Badge className="rounded-full bg-forest text-white border-none mt-4 inline-flex items-center gap-1.5" data-testid="till-timestamp">
                  <Clock className="w-3 h-3" />
                  {new Date(till.redeemed_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </Badge>
                <div className="text-[10px] text-neutral-400 mt-3 tracking-wider uppercase">{till.id}</div>
              </div>
              <p className="text-xs text-neutral-500 mt-3">
                A thank-you from {till.partner_name} — because you showed up.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
